import React, { useState, useEffect } from "react";
import { Row, Col, Card, Input } from "antd";
import ResponsiveTable from "../components/ResponsiveTable";
import LoadingSpinner from "../components/LoadingSpinner";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SessionManagement = () => {
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        const fetchSessions = async () => {
            try {
                // FIXED: Added /api back in, but strictly REMOVED the trailing slash 
                // to prevent FastAPI's 307 proxy redirect loop.
                const response = await fetch(`${API_BASE_URL}/api/sessions`);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Failed to fetch sessions:", errorText);
                    throw new Error("Failed to fetch sessions");
                }

                const sessions = await response.json();

                setData(
                    sessions.map((s) => ({
                        key: s.session_id,
                        username: s.username,
                        project_name: s.project_name,
                        role: s.role,
                        login_time: s.login_time,
                        logout_time: s.logout_time,
                        session_duration: s.session_duration,
                        status: s.status,
                        ip_address: s.ip_address,
                        city: s.city,
                        country: s.country,
                    }))
                );
            } catch (error) {
                console.error("Error fetching sessions:", error);
            } finally {
                setIsInitialLoad(false);
            }
        };

        fetchSessions();

        const interval = setInterval(fetchSessions, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setFilteredData(
            data.filter((item) =>
                Object.values(item)
                    .join(" ")
                    .toLowerCase()
                    .includes(searchText.toLowerCase())
            )
        );
    }, [searchText, data]);

    // Helper to format ISO dates into "31 Aug 2026, 07:57 AM"
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        return date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        });
    };

    // Helper to format "HH:MM:SS" into "X hr Y mins"
    const formatDuration = (durationString) => {
        if (!durationString || durationString === "-") return "-";

        const parts = durationString.split(':');
        if (parts.length === 3) {
            const hrs = parseInt(parts[0], 10);
            const mins = parseInt(parts[1], 10);
            const secs = parseInt(parts[2], 10);

            let result = [];
            if (hrs > 0) result.push(`${hrs} hr${hrs > 1 ? 's' : ''}`);
            if (mins > 0) result.push(`${mins} min${mins > 1 ? 's' : ''}`);
            if (hrs === 0 && mins === 0 && secs > 0) result.push(`${secs} sec${secs > 1 ? 's' : ''}`);

            return result.join(' ') || "0 secs";
        }
        return durationString;
    };

    const columns = [
        {
            title: "USERNAME",
            dataIndex: "username",
            key: "username",
            width: 150, // Added widths to prevent squeezing
        },
        {
            title: "PROJECT",
            dataIndex: "project_name",
            key: "project_name",
            width: 150,
        },
        {
            title: "ROLE",
            dataIndex: "role",
            key: "role",
            width: 110,
        },
        {
            title: "LOGIN TIME",
            dataIndex: "login_time",
            key: "login_time",
            width: 200,
            render: (text) => formatDate(text),
        },
        {
            title: "LOGOUT TIME",
            dataIndex: "logout_time",
            key: "logout_time",
            width: 200,
            render: (text) => (!text || text.toLowerCase() === "active") ? "Active" : formatDate(text),
        },
        {
            title: "SESSION DURATION",
            dataIndex: "session_duration",
            key: "session_duration",
            width: 160,
            render: (text, record) => record.status === "active" ? "-" : formatDuration(text),
        },
        {
            title: "IP ADDRESS",
            dataIndex: "ip_address",
            key: "ip_address",
            width: 140,
        },
        {
            title: "CITY",
            dataIndex: "city",
            key: "city",
            width: 120,
            render: (text) => text || "-",
        },
        {
            title: "COUNTRY",
            dataIndex: "country",
            key: "country",
            width: 100,
            render: (text) => text || "-",
        },
        {
            title: "STATUS",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => (
                <span
                    style={{
                        backgroundColor: status === "active" ? "#52c41a" : "#1890ff",
                        color: "white",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        textTransform: "capitalize",
                        whiteSpace: "nowrap"
                    }}
                >
                    {status}
                </span>
            ),
        },
    ];

    return (
        <div className="session-management">
            <Row gutter={[24, 0]}>
                <Col xs="24" xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24"
                        title="Session Management Table"
                        extra={
                            <Input
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 200 }}
                            />
                        }
                    >
                        {isInitialLoad ? (
                            <LoadingSpinner text="Loading records..." />
                        ) : (
                            <ResponsiveTable
                                columns={columns}
                                dataSource={filteredData}
                                rowKey="recordId"
                                pagination={{
                                    pageSize: 20,
                                    showSizeChanger: false,
                                    position: ["bottomCenter"],
                                }}
                                onChange={() => {
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className="ant-border-space"
                                scroll={{ x: 1400 }} // Forces horizontal scroll instead of squeezing columns
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SessionManagement;